import { Component, OnInit, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ProjectHistoryService, VersionInfo } from '../../services/project-history.service';

export interface HistoryDialogData {
  projectId: string;
  projectName: string;
  currentProjectDataId?: string | null; // Current version ID to disable
  openedFromAnalysisPage?: boolean; // Flag to indicate opened from analysis page
}

@Component({
  selector: 'app-generic-history-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
  ],
  templateUrl: './generic-history-dialog.component.html',
  styleUrl: './generic-history-dialog.component.scss'
})
export class GenericHistoryDialogComponent implements OnInit {
  versions: VersionInfo[] = [];
  loading = false;
  error: string | null = null;
  currentProjectDataId: string | null = null;
  openedFromAnalysisPage: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<GenericHistoryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: HistoryDialogData,
    private projectHistoryService: ProjectHistoryService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.currentProjectDataId = this.data.currentProjectDataId || null;
    this.openedFromAnalysisPage = this.data.openedFromAnalysisPage || false;
    this.loadVersions();
  }

  loadVersions(): void {
    this.loading = true;
    this.error = null;

    this.projectHistoryService.getProjectVersions(this.data.projectId).subscribe({
      next: (response) => {
        this.versions = response.versions;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading versions:', err);
        this.error = err.error?.detail || 'Failed to load project history';
        this.loading = false;
      }
    });
  }

  onCheckVersion(version: VersionInfo): void {
    // Don't allow selecting the current version if opened from analysis page
    if (this.openedFromAnalysisPage && version.id === this.currentProjectDataId) {
      this.snackBar.open(
        'This is the currently active version',
        'Close',
        {
          duration: 2000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        }
      );
      return;
    }
    
    // Close dialog first
    this.dialogRef.close(true);
    
    // Force full page reload with new projectdataId
    // Using window.location.replace() ensures the page always reloads
    const baseUrl = window.location.origin;
    const newUrl = `${baseUrl}/common-ai-analysis/generic/${this.data.projectId}?projectdataId=${version.id}`;
    
    // Use replace() to force reload and replace current history entry
    window.location.replace(newUrl);
  }

  isCurrentVersion(version: VersionInfo): boolean {
    return this.currentProjectDataId !== null && version.id === this.currentProjectDataId;
  }

  isVersionDisabled(version: VersionInfo): boolean {
    // Disable current version only if opened from analysis page
    return this.openedFromAnalysisPage && this.isCurrentVersion(version);
  }

  onDeleteVersion(version: VersionInfo, event: Event): void {
    event.stopPropagation(); // Prevent triggering row click
    
    if (!confirm(`Are you sure you want to delete version ${version.name}?`)) {
      return;
    }

    this.projectHistoryService.deleteVersion(version.id).subscribe({
      next: (response) => {
        if (response.success) {
          // Remove from list
          this.versions = this.versions.filter(v => v.id !== version.id);
          // Show success message
          this.snackBar.open(
            `Version ${version.name} deleted successfully`,
            'Close',
            {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['success-snackbar']
            }
          );
        }
      },
      error: (err) => {
        console.error('Error deleting version:', err);
        this.snackBar.open(
          err.error?.detail || 'Failed to delete version',
          'Close',
          {
            duration: 4000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  formatDuration(seconds?: number | null): string {
    if (seconds == null || seconds < 0) return '—';
    const s = Math.floor(seconds % 60);
    const m = Math.floor((seconds / 60) % 60);
    const h = Math.floor(seconds / 3600);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  }
}
